// Pack page rules, ported from the app so a pack reads the same on the web
// (lib/models/pack.dart, Sound.chip in lib/models/sound.dart and PackView in
// lib/screens/sounds/packs.dart, flowstate master).
//
// Decision 15: rights reserved with no price is never free. In a pack, an
// unpriced rights-reserved sound is a *bonus* sound that comes with buying
// the pack: it is never a $0 buy, never "License $0.00", never "Free", and
// never counted as for sale.
import { prettySessionName } from "./sessionName.js";
import { displayKey, keyName } from "./musicKey.js";
import { chipPriceUsd } from "./sale.js";

export const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

// "one_shot", "one-shot", "oneshot" → one-shot; anything else is a loop.
export function isOneShot(loop) {
  return /^one[-_ ]?shot$/i.test(String((loop && (loop.asset_kind || loop.kind)) || ""));
}

export const kindLabel = (loop) => (isOneShot(loop) ? "One-shot" : "Loop");

const price = (loop) => {
  const n = Number(loop && loop.price_usd);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// The viewer can take everything in the pack: bought, made, or all free
// (Pack.ownedBy).
export function packOwnedBy(pack, me) {
  if (!pack) return false;
  const paid = Number((pack.summary && pack.summary.paid_count) || 0);
  return !!pack.purchased || (!!me && me === pack.curator_username) || paid === 0;
}

// One sound's access (PackSound.toSound). `packOwned` (bought or made the
// pack) makes every non-free sound owned.
export function packSound(loop, { packOwned = false } = {}) {
  const free = !!loop.is_free;
  const p = price(loop);
  const forSale = !free && p > 0 && loop.for_sale !== false;
  // The server flags bonus sounds; an older response without the flag that
  // sends a $0 price for a paid snapshot means the same (never a $0 buy).
  const flagged = "is_bonus" in loop ? !!loop.is_bonus : !loop.takedown && p === 0;
  const bonus = !free && !forSale && flagged;
  const owned = !free && (!!loop.owned || !!packOwned);
  return {
    free,
    forSale,
    bonus,
    owned,
    price: forSale ? p : null,
    canDownload: free || owned,
    canBuy: forSale && !owned,
  };
}

// The pack page's PlayerBar / MiniPlayer item (master's onPlayerCta). Its
// call to action and price pill act through the page, never the shell's
// sound sheets, which a guest doesn't have: Download takes the pack's own
// copy (`download(row)`: a free sound's pack asset, an owned one's WAV) and
// Buy goes through `buy(row)`, the page's licenseLoop, which sends a guest
// to sign-up and back to the pack. A bonus sound, and one neither yours to
// take nor buyable alone, has no call to action (decision 15).
export function packBarItem(item, row, { isOwner = false, download = () => {}, buy = () => {} } = {}) {
  if (!item || !row || !row.access) return item;
  const a = row.access;
  const buyable = !!a.canBuy && !isOwner;
  const out = { ...item };
  if ((a.bonus && !a.owned) || (!a.canDownload && !buyable)) out.cta = null;
  else if (out.cta) {
    out.cta = { ...out.cta, onClick: out.cta.disabled ? null : () => (a.canDownload ? download(row) : buy(row)) };
  }
  if (out.pricePill) out.pricePill = { ...out.pricePill, onClick: () => buy(row) };
  return out;
}

// What the row's licence chip says (Sound.chip): Bonus, Owned, Free, a
// price, or Not for sale.
export function soundChip(access) {
  if (access.bonus && !access.owned) return { kind: "bonus", label: "Bonus" };
  if (access.owned) return { kind: "owned", label: "Owned" };
  if (access.free) return { kind: "free", label: "Free" };
  if (access.forSale && access.price != null) {
    // The frozen price is the sound's checkout minimum: the app's chip rule
    // (chipPriceUsd), the same as a session row's.
    return { kind: "price", label: usd(chipPriceUsd({ checkoutMin: access.price })) };
  }
  return { kind: "notForSale", label: "Not for sale" };
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// "F#m" → "F♯ minor", "D" → "D major" (packKeyName).
export function packKeyName(key) {
  const k = String(key || "").trim();
  if (!k) return "";
  const minor = /m$/.test(k) && !/maj$/i.test(k);
  const root = displayKey(minor ? k.slice(0, -1) : k);
  return `${root} ${minor ? "minor" : "major"}`;
}

export function freeCount(pack) {
  const listed = (pack.loops || []).filter((l) => l.is_free).length;
  const s = Number((pack.summary && pack.summary.free_count) || 0);
  return s > 0 ? s : listed;
}

export function bonusCount(pack) {
  const listed = (pack.loops || []).filter((l) => packSound(l).bonus).length;
  const s = Number((pack.summary && pack.summary.bonus_count) || 0);
  return Math.max(s, listed);
}

// Where the pack's buy field starts, and so the price its Buy button names
// (the app's PackSummary.startUsd): the suggested price when it is above
// the minimum, else the minimum.
export function packStartUsd(summary) {
  const s = summary || {};
  const min = Number(s.pack_price_usd) || 0;
  const sug = s.pack_suggested_usd == null ? null : Number(s.pack_suggested_usd);
  return sug != null && sug > min ? sug : min;
}

// A pack card's price (packPriceLabel): "Owned" when bought, "Free" when
// nothing in it is paid, "Free+" when the curator made the pack free to
// claim, else where its buy sheet starts. A GET /packs row carries the
// summary flat; a pack page nests it.
export function packCardPrice(pack) {
  const p = pack || {};
  const s = p.summary && typeof p.summary === "object" ? p.summary : p;
  if (p.purchased === true) return { kind: "owned", label: "Owned" };
  if (Number(s.paid_count || 0) === 0) return { kind: "free", label: "Free" };
  if (s.free_claim) return { kind: "free", label: "Free+" };
  return { kind: "price", label: usd(packStartUsd(s)) };
}

// MacPacks' "From friends": the server's flag, else a curator the viewer
// follows (the user doc's `friends`, a map or a list).
export function packFromFriend(pack, user) {
  if (!pack) return false;
  if (pack.curator_followed === true) return true;
  const f = user && user.friends;
  const c = pack.curator_username;
  if (!c || !f) return false;
  return Array.isArray(f) ? f.includes(c) : Object.prototype.hasOwnProperty.call(f, c);
}

// ---- The packs grid (MacPacks; PacksGrid in the app's packs.dart). The
// list is GET /packs (at most 100); filters, sorting and search are ours.

// No "Popular" (L7): GET /packs rows carry no purchase count (only the
// pack page's summary does), so it could only ever sort by nothing. The
// app's Popular sorts its feed rows by the same missing count.
export const PACK_SORTS = [
  { value: "newest", label: "Newest" },
  { value: "price", label: "Price" },
];

const time = (p) => {
  const t = Date.parse((p && (p.created_at || p.published_at)) || "");
  return Number.isFinite(t) ? t : 0;
};
const summaryOf = (p) => (p && p.summary && typeof p.summary === "object" ? p.summary : p || {});

export function sortPacks(list, sort = "newest") {
  const out = [...(list || [])];
  if (sort === "price") out.sort((a, b) => packStartUsd(summaryOf(a)) - packStartUsd(summaryOf(b)));
  else out.sort((a, b) => time(b) - time(a));
  return out;
}

// Made in the 7 days before `now` ("New this week").
export function isRecentPack(p, now = Date.now()) {
  const t = time(p);
  return t > 0 && now - t <= 7 * 24 * 3600 * 1000;
}

const packTags = (p) => (Array.isArray(p && p.tags) ? p.tags.filter((t) => typeof t === "string") : []);

/**
 * What the search and chips leave, sorted:
 *   q        title, curator or tag contains it
 *   filter   "free" (all free, or free to claim) | "under10" | null
 *   tag, key one tag, one key
 *   seeAll   "friends" | "recent" | null (a shelf's See all)
 *   friendsOnly  only packs from people you follow (the flat grid)
 */
export function filterPacks(list, { q = "", filter = null, tag = null, key = null, seeAll = null, friendsOnly = false, sort = "newest", isFriend = () => false, now = Date.now() } = {}) {
  const needle = String(q || "").trim().toLowerCase();
  return sortPacks(
    (list || []).filter((p) => {
      if (!p) return false;
      const s = summaryOf(p);
      if (needle) {
        const hay = [p.title, p.curator_username, p.curator_display_name, ...packTags(p)].filter(Boolean).map((x) => String(x).toLowerCase());
        if (!hay.some((x) => x.includes(needle))) return false;
      }
      if (friendsOnly && !isFriend(p)) return false;
      if (tag && !packTags(p).includes(tag)) return false;
      if (key && p.key !== key) return false;
      if (seeAll === "friends" && !isFriend(p)) return false;
      if (seeAll === "recent" && !isRecentPack(p, now)) return false;
      if (filter === "free" && !(Number(s.paid_count || 0) === 0 || s.free_claim)) return false;
      if (filter === "under10" && !(packStartUsd(s) < 10)) return false;
      return true;
    }),
    sort
  );
}

/**
 * MacPacks' shelves: "From friends", then "New this week" (packs not
 * already shown), one row each, then the rest as "All packs".
 */
export function packShelves(sorted, { perRow = 5, isFriend = () => false, now = Date.now() } = {}) {
  const friends = sorted.filter((p) => isFriend(p));
  const shown = new Set(friends.slice(0, perRow).map((p) => p.slug));
  const recent = sorted.filter((p) => isRecentPack(p, now) && !shown.has(p.slug));
  recent.slice(0, perRow).forEach((p) => shown.add(p.slug));
  const rest = sorted.filter((p) => !shown.has(p.slug));
  const out = [];
  if (friends.length) out.push({ id: "friends", title: "From friends", packs: friends.slice(0, perRow), seeAll: "friends" });
  if (recent.length) out.push({ id: "recent", title: "New this week", packs: recent.slice(0, perRow), seeAll: "recent" });
  if (rest.length) out.push({ id: "all", title: "All packs", packs: rest, seeAll: null });
  return out;
}

export function packTagsOf(list) {
  return [...new Set((list || []).flatMap(packTags))].sort();
}

export function packKeysOf(list) {
  return [...new Set((list || []).map((p) => p && p.key).filter(Boolean))].sort();
}

// Sounds buyable on their own (priced, never bonus).
// How many sounds a pack holds: the summary (flat on a /packs row, nested
// on a pack page), else a count the row carries, else its listed loops
// (the app's Pack.count). Null when nothing says.
export function packSoundTotal(pack) {
  const p = pack || {};
  for (const v of [summaryOf(p).total, p.sound_count, p.loop_count]) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return Array.isArray(p.loops) && p.loops.length ? p.loops.length : null;
}

// Search's pack line (home_search_results.dart): "<curator> · N sounds",
// the curator by display name (d20).
export function packSearchSub(pack) {
  const p = pack || {};
  const by = p.curator_display_name || p.curator_username || p.curator || "";
  const n = packSoundTotal(p);
  return [by, n ? `${n} ${n === 1 ? "sound" : "sounds"}` : null].filter(Boolean).join(" · ");
}

export function forSaleCount(pack) {
  return (pack.loops || []).filter((l) => packSound(l).forSale).length;
}

// The stats line. Phone: "20 sounds · 9 free · 1 bonus · 120 BPM · F♯ minor".
// Desktop: loops and one-shots, free, bonus, tempo, key, sales.
export function packStats(pack, { phone = false } = {}) {
  const loops = pack.loops || [];
  const shots = loops.filter(isOneShot).length;
  const nLoops = loops.length - shots;
  const total = Number((pack.summary && pack.summary.total) || 0) || loops.length;
  const free = freeCount(pack);
  const bonus = bonusCount(pack);
  const sold = Number((pack.summary && pack.summary.purchases_count) || 0);
  const parts = [];
  if (phone) parts.push(plural(total, "sound", "sounds"));
  else {
    if (nLoops > 0) parts.push(plural(nLoops, "loop", "loops"));
    if (shots > 0) parts.push(plural(shots, "one-shot", "one-shots"));
  }
  if (free > 0) parts.push(`${free} free`);
  if (bonus > 0) parts.push(`${bonus} bonus`);
  if (pack.tempo) parts.push(`${Math.round(Number(pack.tempo))} BPM`);
  if (pack.key) parts.push(packKeyName(pack.key));
  if (!phone && sold > 0) parts.push(`${sold} sold`);
  return parts.join(" · ");
}

// Paid sounds the viewer bought on their own (the pack itself not bought).
export function ownedSinglyCount(pack) {
  return (pack.loops || []).filter((l) => !l.is_free && !l.takedown && !!l.owned).length;
}

// The pack's two buttons (PackView._buttons), web flavour: the web is the
// checkout (decision 7), so Buy is real.
//
// The download (GET /api/packs/<slug>/download, cloudloop-flask
// download_pack_bundle) is the Ableton Live set with the sounds the viewer
// may take: everything for an owner, else the free sounds plus any bought
// singly. It needs an account, so signed in the second button hands out
// that set; signed out, "Get the N free sounds" hands out the free WAVs
// one by one.
//   primary:   { kind: "buy" | "claim" | "status", label }
//   secondary: { kind: "free" | "bundle", label } or null
export function packActions(pack, me, { compact = false } = {}) {
  const s = pack.summary || {};
  const owned = packOwnedBy(pack, me);
  const mine = !!me && me === pack.curator_username;
  const allFree = Number(s.paid_count || 0) === 0;
  const free = freeCount(pack);
  const freeLabel = compact
    ? `Get ${free} free`
    : `Get the ${free} free ${free === 1 ? "sound" : "sounds"}`;
  if (owned) {
    const label = mine ? "Your pack" : allFree ? "Free pack" : "Owned";
    let secondary = null;
    if (me) secondary = { kind: "bundle", label: "Download all" };
    else if (free > 0) secondary = { kind: "free", label: freeLabel };
    return { primary: { kind: "status", label }, secondary };
  }
  const primary = s.free_claim
    ? { kind: "claim", label: "Get it free" }
    : { kind: "buy", label: `Buy pack · ${usd(packStartUsd(s))}` };
  let secondary = null;
  if (free > 0) secondary = { kind: me ? "bundle" : "free", label: freeLabel };
  else if (me && ownedSinglyCount(pack) > 0) {
    const n = ownedSinglyCount(pack);
    secondary = {
      kind: "bundle",
      label: compact ? "Download yours" : `Download your ${plural(n, "sound", "sounds")}`,
    };
  }
  return { primary, secondary };
}

// "In the download" (MacPack) and its one-line phone pill (PackPage), in
// the app's words (PackView._downloadNote).
export const DOWNLOAD_NOTE_COMPACT = "Ableton Live set (Live 11+), WAV files, credits";

export function packDownloadNote(pack) {
  const s = (pack && pack.summary) || {};
  const listed = ((pack && pack.loops) || []).filter((l) => !l.takedown);
  const counted = Number.isFinite(s.loop_count) && Number.isFinite(s.one_shot_count);
  const shots = counted ? s.one_shot_count : listed.filter(isOneShot).length;
  const loops = counted ? s.loop_count : listed.length - shots;
  const parts = [];
  if (loops > 0) parts.push(plural(loops, "loop", "loops"));
  if (shots > 0) parts.push(plural(shots, "one-shot", "one-shots"));
  const wavs = parts.length
    ? `${parts.join(" and ")} as WAV, sorted into folders`
    : "Every sound as WAV";
  return [
    { icon: "live", t: "Ableton Live set, Live 11 or later" },
    { icon: "file", t: wavs },
    { icon: "credit", t: "A credits and license file" },
  ];
}

// "Friday Jam Pack" → ["Friday", "Jam"] for the art's two lines
// (packArtLines): drop a trailing "Pack" / "Vol. 1", split the words in two,
// hyphenate one long word.
export function packArtLines(title) {
  let t = String(title || "")
    .replace(/\s+(Pack|Vol\.?\s*\d+)$/i, "")
    .trim();
  if (!t) t = String(title || "");
  const fit = 9;
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length === 0) return ["", ""];
  if (words.length === 1) {
    const w = words[0];
    if (w.length <= fit) return [w, ""];
    const cut = Math.floor(w.length * 0.55);
    return [`${w.slice(0, cut)}-`, w.slice(cut)];
  }
  const half = Math.ceil(words.length / 2);
  const first = words.slice(0, half).join(" ");
  let second = words.slice(half).join(" ");
  if (words.length === 2 && second.length > fit) second = second.slice(0, 4);
  return [first, second];
}

// A pack's picture when its artwork is an image URL; "art:<seed>" (Make
// pack's Change art) and empty mean the two-glow tile.
export function packPicture(pack) {
  const a = String((pack && pack.artwork) || "");
  return /^(https?:|data:image\/|\/)/.test(a) ? a : null;
}

export function packArtSeed(pack) {
  const a = String((pack && pack.artwork) || "");
  return a.startsWith("art:") && a.length > 4 ? a.slice(4) : (pack && pack.slug) || "";
}

// What a share says about the pack, never counting bonus sounds as for sale.
export function packShareText(pack, curatorLabel) {
  const s = pack.summary || {};
  const total = Number(s.total || 0) || (pack.loops || []).length;
  const bits = [plural(total, "sound", "sounds")];
  const free = freeCount(pack);
  if (free) bits.push(`${free} free`);
  const sale = forSaleCount(pack);
  if (sale) bits.push(`${sale} for sale from ${usd(s.from_price_usd)}`);
  const bonus = bonusCount(pack);
  if (bonus) bits.push(`${bonus} bonus`);
  return `${pack.title}, a pack by ${curatorLabel} on cloudloop (${bits.join(" · ")})`;
}

// A sound's name: the snapshot's filename without ".wav"; an unnamed one
// (the server's "<hash8>.wav") reads "Loop 4f2a1c" (soundTitle).
export function soundTitle(loop) {
  const hash = String(loop.loop_hash || loop.hash || "");
  const name = String(loop.filename || loop.name || "")
    .replace(/\.wav$/i, "")
    .trim();
  if (name && !(hash && hash.startsWith(name) && name.length >= 6)) return name;
  return `Loop ${hash.slice(0, 6)}`;
}

// Pitch class (loop metadata) → "F♯m", the one spelling every page uses
// (musicKey.js).
export function keyLabel(md) {
  return md ? keyName(md.key_pitch_class, md.key_is_minor) : null;
}

// "2 bars" for a loop whose length and tempo are known (packSoundBars).
export function barsLabel(seconds, bpm) {
  const d = Number(seconds);
  const t = Number(bpm);
  if (!(d > 0) || !(t > 0)) return null;
  const n = Math.round((d * t) / 60 / 4);
  return n > 0 ? plural(n, "bar", "bars") : null;
}

// MacPack's header line after the curator: "recorded at Night drive". The
// server sends the pack's source session name (a raw timestamp name reads
// as its date); a server-written byline wins when there is one.
export function packByline(pack) {
  if (!pack) return "";
  const own = typeof pack.byline === "string" ? pack.byline.trim() : "";
  if (own) return own;
  const src = prettySessionName(pack.source_session_name || "");
  return src ? `recorded at ${src}` : "";
}
